import { ReactNode, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { rucksack, theme, Box } from '@black-bear-energy/rucksack'
import {
  NAV_TRANSITION,
  NAV_WIDTH_EXPANDED,
  NAV_WIDTH_COLLAPSED,
  HEADER_HEIGHT,
} from './constants'
import { useNavigation } from './NavigationContext'
import { useWideContentValue } from './WideContentContext'

/** Props for {@link ContentContainer}. */
interface ContentContainerProps {
  /** Page content rendered inside the scrollable main area. */
  children: ReactNode
}

/** Responsive content area that adjusts for side navigation width. */
export function ContentContainer({ children }: ContentContainerProps) {
  const { expanded } = useNavigation()
  const wideContent = useWideContentValue()
  const scrollRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()

  // When the current path changes, scroll to the top of the container
  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0)
  }, [pathname])
  const borderRadius = `${rucksack.shape.borderRadius} 0 0 0`

  return (
    <Box
      component="main"
      sx={{
        position: 'fixed',
        top: `${HEADER_HEIGHT}px`,
        left: `${expanded ? NAV_WIDTH_EXPANDED : NAV_WIDTH_COLLAPSED}px`,
        right: 0,
        bottom: 0,
        backgroundColor: rucksack.palette.background.page,
        borderRadius,
        transition: theme.transitions.create('left', NAV_TRANSITION),
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius,
          boxShadow: 'inset 0 1px 4px 0 rgba(0, 0, 0, 0.25)',
          pointerEvents: 'none',
          zIndex: 1,
        },
      }}
    >
      <Box
        ref={scrollRef}
        sx={{
          height: '100%',
          overflowY: 'auto',
          borderRadius,
        }}
      >
        <Box
          sx={{
            padding: '2rem',
            margin: '0 auto',
            minHeight: '100%',
            boxSizing: 'content-box',
            ...(!wideContent && { maxWidth: '80rem' }),
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}
